import React, {useEffect, useRef, useState} from "react";
import {routePath} from "../auth/roles";
import {formatUnreadBadge, useNotificationUnreadCount, useRoute} from "../hooks";
import {NotificationFeed} from "./NotificationFeed";
import type {NotificationScope} from "./notificationScope";

const POPOVER_PAGE_SIZE = 20;

/** Колокольчик в шапке кабинета: число непрочитанных и выпадающая лента свежих уведомлений. */
export function NotificationsBell({scope}: {scope: NotificationScope}): JSX.Element {
  const [open, setOpen] = useState(false);
  const route = useRoute();
  const wrapRef = useRef<HTMLDivElement>(null);
  // Открытая лента (здесь или на странице истории) сама приносит счётчик; иначе его опрашивает колокольчик.
  const feedShown = open || routePath(route) === scope.feedRoute;
  const unreadBadge = formatUnreadBadge(useNotificationUnreadCount(scope.api.list, feedShown));
  const label = unreadBadge ? `Уведомления, непрочитанных: ${unreadBadge}` : "Уведомления";

  // Переход по ссылке из ленты закрывает её.
  useEffect(() => setOpen(false), [route]);

  useEffect(() => {
    if (!open) return;
    const onPointer = (event: MouseEvent): void => {
      if (wrapRef.current && !wrapRef.current.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="merchant-bell-wrap" ref={wrapRef}>
      <button
        type="button"
        className={`merchant-notify-bell${open ? " active" : ""}`}
        aria-label={label}
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => setOpen((value) => !value)}
        data-testid="merchant-notifications-bell"
      >
        <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" focusable="false">
          <path
            d="M12 3a6 6 0 0 0-6 6v3.6L4.4 15.5a1 1 0 0 0 .9 1.5h13.4a1 1 0 0 0 .9-1.5L18 12.6V9a6 6 0 0 0-6-6zm0 18a2.5 2.5 0 0 0 2.4-1.8H9.6A2.5 2.5 0 0 0 12 21z"
            fill="currentColor"
          />
        </svg>
        {unreadBadge && (
          <span className="merchant-notify-count" data-testid="merchant-notifications-bell-unread">
            {unreadBadge}
          </span>
        )}
      </button>
      {open && (
        <div className="merchant-bell-popover" role="dialog" aria-label="Уведомления" data-testid="merchant-notifications-popover">
          <div className="merchant-bell-popover-head">
            <strong>Уведомления</strong>
            <a href={scope.settingsRoute} data-testid="merchant-notifications-settings-link">
              Настроить
            </a>
          </div>
          <div className="merchant-bell-popover-body">
            <NotificationFeed scope={scope} pageSize={POPOVER_PAGE_SIZE}/>
          </div>
          <a className="merchant-bell-popover-foot" href={scope.feedRoute} data-testid="merchant-notifications-all">
            Вся история
          </a>
        </div>
      )}
    </div>
  );
}
